/**
 * Conciliação de Demonstração (GET/POST /demo-reconciliation/...).
 *
 * Pedidos de demonstração enviados à cliente são confrontados com os
 * recebimentos de materiais (o que voltou). O saldo — o que ficou com a
 * cliente — vira uma proposta no CRM; sem saldo, a conciliação é encerrada sem
 * cobrança. O cálculo do saldo é SEMPRE do backend (endpoint de preview).
 */

/** Cliente com pedidos de demonstração ainda não conciliados. */
export interface DemoPendingCustomer {
  customer_id: number;
  customer_name: string;
  document: string;
  orders_count: number;
  /** Valor líquido dos produtos enviados (sem frete). */
  products_total: number;
  oldest_order_date: string; // YYYY-MM-DD
  latest_order_date: string; // YYYY-MM-DD
  /** Vendedora do pedido mais recente. */
  seller_name: string;
}

export interface DemoOrderItem {
  order_item_id: number;
  sku: string;
  product_id: number | null;
  description: string;
  quantity: number;
  returned_quantity: number;
  unit_price: number;
  discount_percent: number;
  net_unit_price: number;
  is_gift: boolean;
}

export interface DemoOrder {
  order_id: number;
  order_date: string; // YYYY-MM-DD
  order_status_id: number;
  order_status: string;
  seller_name: string;
  proposal_num: number | null;
  /** Informativo: o frete dos pedidos de demonstração NÃO é cobrado. */
  shipping_price: number;
  items: DemoOrderItem[];
}

export interface DemoReceiptItem {
  material_receipt_item_id: number;
  product_id: number;
  sku: string;
  description: string;
  quantity: number;
  /** Vínculo já gravado pela logística — trava o select nesse pedido. */
  order_id: number | null;
  /** Sugestão: pedido demo mais recente da cliente com o SKU e saldo. */
  suggested_order_id: number | null;
}

export interface DemoReceipt {
  material_receipt_id: number;
  receipt_date: string | null; // YYYY-MM-DD
  notes: string;
  items: DemoReceiptItem[];
}

export interface DemoWorkspace {
  customer_id: number;
  customer_name: string;
  document: string;
  seller_id: number | null;
  seller_name: string;
  company_id: number | null;
  orders: DemoOrder[];
  receipts: DemoReceipt[];
}

export interface DemoSelection {
  material_receipt_item_id: number;
  order_id: number;
}

export interface DemoSelectionPayload {
  selections: DemoSelection[];
}

export interface DemoBalanceLine {
  order_id: number;
  sku: string;
  product_id: number | null;
  description: string;
  is_gift: boolean;
  sent_quantity: number;
  returned_quantity: number;
  balance_quantity: number;
  unit_price: number;
  discount_percent: number;
  net_unit_price: number;
  line_total: number;
}

export interface DemoBalance {
  lines: DemoBalanceLine[];
  total: number;
  has_balance: boolean;
  seller_name: string;
  warnings: string[];
}

export type DemoReconciliationStatus = 'proposal_created' | 'closed_no_charge';

export const DEMO_RECONCILIATION_STATUS_LABEL: Record<DemoReconciliationStatus, string> = {
  proposal_created: 'Proposta gerada',
  closed_no_charge: 'Encerrado sem cobrança',
};

export const demoStatusLabel = (status: string): string =>
  DEMO_RECONCILIATION_STATUS_LABEL[status as DemoReconciliationStatus] ?? status;

export interface DemoConfirmResult {
  demo_reconciliation_id: number;
  status: DemoReconciliationStatus;
  comercial_proposal_id: number | null;
  payment_link: string;
  pix_link: string;
  shipping_price: number;
  sell_total: number;
  products_total: number;
  cancelled_order_ids: number[];
  warnings: string[];
}

export interface DemoReconciliationHistory {
  demo_reconciliation_id: number;
  customer_id: number;
  customer_name: string;
  seller_name: string;
  products_total: number;
  status: DemoReconciliationStatus;
  comercial_proposal_id: number | null;
  payment_link: string;
  pix_link: string;
  created_at: string; // YYYY-MM-DDTHH:mm:ss (horário de Brasília, sem fuso)
  created_by: string;
  order_ids: number[];
}

export interface DemoReconciliationHistoryPage {
  items: DemoReconciliationHistory[];
  total: number;
  page: number;
  limit: number;
}
